import { useState, useEffect } from 'react'
import { Outlet, NavLink, useParams, useLocation, useNavigate } from 'react-router-dom'
import { Home, Apple, UserRound, Activity, MonitorSmartphone } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'

export default function PatientPortalLayout() {
    const { token } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const [cabinetName, setCabinetName] = useState("Espace Patient")
    const [features, setFeatures] = useState({ meals: true, activity: true, weight: true })

    // Injection dynamique du manifeste PWA personnalisé
    useEffect(() => {
        let manifestLink = document.querySelector('link[rel="manifest"]');
        if (!manifestLink) {
            manifestLink = document.createElement('link');
            manifestLink.rel = 'manifest';
            document.head.appendChild(manifestLink);
        }
        manifestLink.href = `/api/portal/manifest?token=${token}`;

        // Optimisations d'affichage pour mobile (Standalone conforme W3C)
        const metaCapability = document.createElement('meta');
        metaCapability.name = 'mobile-web-app-capable';
        metaCapability.content = 'yes';
        document.head.appendChild(metaCapability);

        const metaStatus = document.createElement('meta');
        metaStatus.name = 'apple-mobile-web-app-status-bar-style';
        metaStatus.content = 'black-translucent';
        document.head.appendChild(metaStatus);


        return () => {
            // Nettoyage si le navigateur quitte le portail (ex: retour à l'admin)
            if (manifestLink) manifestLink.removeAttribute('href');
            if (metaCapability.parentNode) metaCapability.parentNode.removeChild(metaCapability);
            if (metaStatus.parentNode) metaStatus.parentNode.removeChild(metaStatus);
        };
    }, [token]) 
    
    // Fonction d'acquisition réutilisable
    const fetchPortalConfig = () => {
        api.get(`/api/portal/${token}/dashboard`)
           .then(res => {
               setCabinetName(res.data.cabinet_name);
               if (res.data.features) {
                   setFeatures(res.data.features);
               }
           })
           .catch(() => {})
    }

    useEffect(() => {
        fetchPortalConfig();

        // Écouteur pour rafraîchir l'espace dès que l'application mobile passe au premier plan
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchPortalConfig();
            }
        };

        window.addEventListener('focus', fetchPortalConfig);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', fetchPortalConfig);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [token]) // attention espace

    // Redirection de sécurité en cas de révocation des droits d'accès
    useEffect(() => {
        const path = location.pathname;
        if (!features.meals && path.endsWith('/carnet')) {
            navigate(`/p/${token}`, { replace: true });
            toast("L'accès au carnet alimentaire a été désactivé par votre praticien.", { icon: 'ℹ️' });
        }
        if (!features.activity && path.endsWith('/activite')) {
            navigate(`/p/${token}`, { replace: true });
            toast("L'accès au suivi d'activités a été désactivé par votre praticien.", { icon: 'ℹ️' });
        }
    }, [features, location.pathname, token, navigate]) // attention espace


    const navItems = [
        { to: `/p/${token}`, icon: Home, label: 'Accueil', end: true }
    ]


    // Affichage conditionnel non-frustrant
    if (features.meals) {
        navItems.push({ to: `/p/${token}/carnet`, icon: Apple, label: 'Mon Carnet' });
    }
    if (features.activity) {
        navItems.push({ to: `/p/${token}/activite`, icon: Activity, label: 'Activité' });
    }



    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans text-gray-800">
            
            {/* Header Mobile minimaliste */}
            <header className="bg-white px-4 py-4 shadow-sm flex items-center justify-between sticky top-0 z-20">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-sage-100 rounded-full flex items-center justify-center">
                        <UserRound className="w-4 h-4 text-sage-700" />
                    </div>
                    <span className="font-serif font-bold text-sage-800 truncate max-w-[250px]">{cabinetName}</span>
                </div>
            </header>

            {/* Contenu principal (avec padding-bottom pour pas être caché par la navBar) */}
            <main className="flex-1 overflow-y-auto pb-24 p-4 max-w-lg mx-auto w-full">
                <Outlet />
            </main>

            {/* Bottom Navigation Bar (App-Like) */}
            <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pb-safe z-30">
                <div className="flex justify-around items-center h-16 max-w-lg mx-auto px-2">
                    {navItems.map(item => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            className={({ isActive }) => `
                                flex flex-col items-center justify-center w-full h-full gap-1 transition-colors
                                ${isActive ? 'text-sage-600' : 'text-gray-400 hover:text-gray-600'}
                            `}
                        >
                            <item.icon className={`w-6 h-6 ${window.location.pathname === item.to ? 'fill-sage-100/50' : ''}`} />
                            <span className="text-[10px] font-bold">{item.label}</span>
                        </NavLink>
                    ))}
                </div>
            </nav>
        </div>
    )
}